import { useState } from 'react'

export interface ModalState<T> {
  isOpen: boolean
  data: T | null
  open: (data?: T) => void
  close: () => void
  toggle: () => void
}

/**
 * Modal open/close/data hook.
 * Handles isOpen state and any data to pass into the modal.
 *
 * @example
 * const modal = useModal<{ offerId: string }>()
 * modal.open({ offerId: 'clxxx' })
 * // in JSX: <ResponsiveModal open={modal.isOpen} onClose={modal.close} data={modal.data} />
 */
export function useModal<T = undefined>(): ModalState<T> {
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<T | null>(null)

  const open = (payload?: T) => {
    if (payload !== undefined) setData(payload)
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setData(null)
  }

  const toggle = () => setIsOpen((prev) => !prev)

  return { isOpen, data, open, close, toggle }
}
